import { Injectable } from '@angular/core';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { ShowToasterService } from 'src/app/bar/services/show-toaster.service';
import { Permissions } from '@local/client-contracts';
import { BaseFile, BlobsStorageService } from 'src/app/bar/services/blobs/blobs-storage.service';
import { getExtensionByFileName, getIconByExtension } from '@local/ts-infra';
import { Logger } from '@unleash-tech/js-logger';
import { LogService } from '@shared/services';
import { generateId } from '@local/common-web';
import { ManualPromise } from '@local/common';

@Injectable()
export class UploadBlobService {
  private readonly TOASTER_ID = 'file-error';
  private logger: Logger;

  readonly UPLOAD_ERROR = 'Upload failed. Please remove the file and try again';

  constructor(
    logService: LogService,
    private showToasterService: ShowToasterService,
    private blobsService: BlobsService,
    private blobsStorageService: BlobsStorageService<BaseFile>
  ) {
    this.logger = logService.scope('UploadBlobsService');
  }

  async onUploadFile(file: File, followingId: string, queryParams: string = '', manualPromise?: ManualPromise<any>) {
    let blobId = generateId();
    this.blobsStorageService.addAttachment(followingId, this.buildUploadingFile(file, blobId));

    try {
      if (manualPromise) {
        await manualPromise;
        if (!this.isRelevantFile(blobId, followingId)) {
          return;
        }
      }

      const shareOptions: Permissions.ShareOptions = {
        level: 'Following',
        followingIds: [followingId],
      };

      const currentBlobId = (await this.blobsService.create(file.name, file.type, shareOptions))?.id;
      this.blobsStorageService.updateBlobId(followingId, blobId, currentBlobId);
      blobId = currentBlobId;

      const uploadResponse = await this.blobsService.upload(file, blobId, queryParams);
      if (uploadResponse.status >= 400) {
        if (!this.isRelevantFile(blobId, followingId)) {
          return;
        }
        this.onUploadError(this.UPLOAD_ERROR);
        this.blobsStorageService.updateAttachmentStatus(followingId, blobId, 'error');
        this.logger.error('failed to upload blob', uploadResponse);
        return;
      }

      this.blobsStorageService.updateAttachmentStatus(followingId, blobId, 'completed');
    } catch (error) {
      this.logger.error('failed to upload blob', error);
      this.blobsStorageService.updateAttachmentStatus(followingId, blobId, 'error');
    }
  }

  private isRelevantFile(blobId: string, followingId: string): boolean {
    return !!this.blobsStorageService.getAttachment(followingId, blobId);
  }

  private buildUploadingFile(file: File, blobId?: string): BaseFile {
    const { name, size, type } = file;
    const icon = getIconByExtension(getExtensionByFileName(name));
    return { blobId, name, size, mimeType: type, icon, status: 'uploading' };
  }

  onUploadError(message: string) {
    this.showToasterService.showErrorToaster(this.TOASTER_ID, message);
  }
}
